import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const dropdownThumbnails: Record<string, string> = {
  shoes: "https://readdy.ai/api/search-image?query=Single%20pair%20of%20modern%20white%20sneakers%20isolated%20on%20warm%20cream%20background%20clean%20minimal%20product%20photography%20soft%20shadow%20ecommerce%20style&width=100&height=100&seq=nav-thumb-shoes-v5&orientation=squarish",
  "hoodies-sweaters": "https://readdy.ai/api/search-image?query=Single%20beige%20oversized%20hoodie%20folded%20neatly%20on%20cream%20surface%20minimal%20product%20photography%20warm%20lighting%20clean%20aesthetic&width=100&height=100&seq=nav-thumb-hoodies-v5&orientation=squarish",
  "t-shirts": "https://readdy.ai/api/search-image?query=Single%20white%20cotton%20t-shirt%20folded%20on%20cream%20textured%20background%20minimal%20product%20photography%20soft%20daylight%20ecommerce&width=100&height=100&seq=nav-thumb-tshirts-v5&orientation=squarish",
  jackets: "https://readdy.ai/api/search-image?query=Single%20olive%20green%20bomber%20jacket%20on%20wooden%20hanger%20against%20cream%20wall%20minimal%20product%20photography%20warm%20tones%20clean&width=100&height=100&seq=nav-thumb-jackets-v5&orientation=squarish",
  "pants-shorts": "https://readdy.ai/api/search-image?query=Single%20pair%20of%20beige%20cargo%20pants%20folded%20on%20cream%20surface%20minimal%20product%20photography%20warm%20lighting%20ecommerce&width=100&height=100&seq=nav-thumb-pants-v5&orientation=squarish",
  headwear: "https://readdy.ai/api/search-image?query=Single%20black%20baseball%20cap%20on%20cream%20surface%20minimal%20product%20photography%20clean%20aesthetic%20warm%20lighting&width=100&height=100&seq=nav-thumb-headwear-v5&orientation=squarish",
  sets: "https://readdy.ai/api/search-image?query=Two%20matching%20beige%20outfit%20pieces%20folded%20together%20on%20cream%20surface%20minimal%20product%20photography%20warm%20tones%20clean&width=100&height=100&seq=nav-thumb-sets-v5&orientation=squarish",
  underwear: "https://readdy.ai/api/search-image?query=Soft%20cotton%20undergarment%20folded%20on%20cream%20textured%20fabric%20minimal%20product%20photography%20warm%20lighting%20clean%20aesthetic&width=100&height=100&seq=nav-thumb-underwear-v5&orientation=squarish",
  jersey: "https://readdy.ai/api/search-image?query=Single%20red%20sports%20jersey%20folded%20on%20cream%20surface%20minimal%20product%20photography%20warm%20lighting%20clean%20aesthetic&width=100&height=100&seq=nav-thumb-jersey-v5&orientation=squarish",
  accessories: "https://readdy.ai/api/search-image?query=Two%20stylish%20sunglasses%20on%20cream%20surface%20minimal%20product%20photography%20warm%20lighting%20clean%20aesthetic%20ecommerce&width=100&height=100&seq=nav-thumb-accessories-v5&orientation=squarish",
  bags: "https://readdy.ai/api/search-image?query=Single%20tan%20leather%20crossbody%20bag%20on%20cream%20surface%20minimal%20product%20photography%20warm%20lighting%20clean%20aesthetic&width=100&height=100&seq=nav-thumb-bags-v5&orientation=squarish",
  belts: "https://readdy.ai/api/search-image?query=Single%20brown%20leather%20belt%20coiled%20on%20cream%20surface%20minimal%20product%20photography%20warm%20lighting%20clean&width=100&height=100&seq=nav-thumb-belts-v5&orientation=squarish",
  perfumes: "https://readdy.ai/api/search-image?query=Single%20elegant%20glass%20perfume%20bottle%20on%20cream%20surface%20minimal%20product%20photography%20warm%20lighting%20clean%20aesthetic&width=100&height=100&seq=nav-thumb-perfumes-v5&orientation=squarish",
  "other-stuff": "https://readdy.ai/api/search-image?query=Assorted%20small%20home%20gadgets%20arranged%20on%20cream%20surface%20minimal%20product%20photography%20warm%20lighting%20clean%20aesthetic&width=100&height=100&seq=nav-thumb-other-v5&orientation=squarish",
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b-2 border-background-200/80" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <div className="relative max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="font-heading text-xl font-bold text-foreground-950 tracking-tight">
              itaobuy<span className="text-primary-500">.</span>wtf
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Home */}
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/article/itaobuy-spreadsheet-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-foreground-300/50 bg-background-50 text-sm font-semibold text-foreground-800 hover:border-foreground-400 hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line text-sm" />
                <span>Browse Categories</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-xs" />
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-[420px] max-h-[520px] overflow-y-auto bg-background-50 border-2 border-background-200/80 rounded-xl z-50 py-2" style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
                  <div className="grid grid-cols-2 gap-0.5 px-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer group"
                      >
                        <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-background-200">
                          <img
                            src={dropdownThumbnails[cat.slug] || dropdownThumbnails["other-stuff"]}
                            alt={cat.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground-900 truncate">{cat.name}</div>
                          <div className="text-xs text-foreground-500">{cat.items} items</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              itaobuy sheet
              <i className="ri-arrow-right-up-line ml-1.5 text-xs" />
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation menu"
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay — rendered OUTSIDE <nav> so backdrop-filter doesn't trap its fixed positioning */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex flex-col">
          {/* Top bar (matches navbar height) */}
          <div className="h-16 shrink-0 flex items-center justify-between px-4 bg-background-50 border-b-2 border-background-200/80">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="font-heading text-xl font-bold text-foreground-950 tracking-tight cursor-pointer"
            >
              itaobuy<span className="text-primary-500">.</span>wtf
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation menu"
              className="w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto bg-background-50">
            <div className="px-4 py-4 flex flex-col gap-4">
              {/* Quick Nav Links */}
              <div className="flex gap-2">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold text-center cursor-pointer"
                >
                  <i className="ri-home-5-line mr-1.5" />Home
                </Link>
                <Link
                  to="/article/itaobuy-spreadsheet-guide"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center cursor-pointer"
                >
                  <i className="ri-book-open-line mr-1.5" />Guide
                </Link>
              </div>

              {/* Category Grid */}
              <p className="text-xs text-foreground-500 uppercase tracking-widest font-semibold mt-1">Shop by Category</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2.5 p-2.5 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={dropdownThumbnails[cat.slug] || dropdownThumbnails["other-stuff"]}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-foreground-900 truncate">{cat.name}</div>
                      <div className="text-[10px] text-foreground-500">{cat.items} items</div>
                    </div>
                  </a>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col gap-2 pt-3 mt-1 border-t-2 border-background-200/60">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center cursor-pointer"
                >
                  itaobuy sheet
                  <i className="ri-arrow-right-up-line ml-1.5 text-xs" />
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full py-3.5 rounded-full bg-foreground-950 text-background-50 text-sm font-bold text-center cursor-pointer"
                >
                  buy it now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}